import { Controller } from 'stimulus'
import {
  Chart,
  DoughnutController,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  ArcElement,
  Tooltip,
  Legend,
  Title,
  Filler
} from 'chart.js';

Chart.register(
  DoughnutController,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  ArcElement,
  Tooltip,
  Legend,
  Title,
  Filler
);

import 'chartjs-adapter-luxon';

export default class extends Controller {
  static targets = [
    'accumulatedProgression',
    'tasksChart',
    'progressChart',
    'messagesChart',
    'programStatusesChart'
  ]

  connect() {
    this.renderMessagesChart();
    this.renderProgressChart();
    this.renderTasksStatusChart();
    this.renderProgramStatusChart();
    this.renderAccumulatedProgressionChart();
  }

  renderMessagesChart() {
    if (!this.hasMessagesChartTarget) return;

    const emptyData = JSON.parse(this.messagesChartTarget.dataset.empty);
    const chartData = JSON.parse(this.messagesChartTarget.dataset.chart);

    const {
      center_labels: {
        primary: { value: centerLabel } = {},
        secondary: { value: centerSubLabel, color: centerSubLabelColor } = {}
      } = {}
    } = chartData;

    let values = [], labels = [], colors = [];

    for (const key of ['social', 'ai_assistant', 'in_task']) {
      const segment = chartData[key];
      if (segment && segment.value != null) {
        values.push(segment.value);
        labels.push(segment.label);
        colors.push(segment.color);
      }
    }

    const total = values.reduce((sum, val) => sum + val, 0);
    if (total === 0) {
      values = emptyData.values;
      colors = emptyData.colors;
      labels = [];
    }

    const doughnutChartOptions = {
      dataValues: values,
      labels,
      labelColors: colors,
      centerLabel,
      centerSubLabel,
      centerSubLabelColor
    };

    this.renderDoughnutChart(this.messagesChartTarget, doughnutChartOptions);
  }

  renderProgressChart() {
    if (!this.hasProgressChartTarget) return;

    const chartData = JSON.parse(this.progressChartTarget.dataset.chart);
    const {
      progress: { label: progressLabel, value: progressValue, color: progressColor },
      remainder: { label: remainderLabel, value: remainderValue, color: remainderColor },
      center_labels: {
        primary: { value: centerLabel },
        secondary: { value: centerSubLabel, color: centerSubLabelColor },
      },
      expected_progress
    } = chartData;

    let dataValues, labels, labelColors;

    if (expected_progress) {
      const {
        label: expectedLabel,
        value: expectedValue,
        color: expectedColor,
      } = expected_progress;

      dataValues = [progressValue, expectedValue, remainderValue];
      labels = [progressLabel, expectedLabel, remainderLabel];
      labelColors = [progressColor, expectedColor, remainderColor];
    } else {
      dataValues = [progressValue, remainderValue];
      labels = [progressLabel, remainderLabel];
      labelColors = [progressColor, remainderColor];
    }

    const displayPercentage = true;

    const doughnutChartOptions = {
      dataValues,
      labels,
      labelColors,
      centerLabel,
      centerSubLabel,
      centerSubLabelColor,
      displayPercentage
    };

    this.renderDoughnutChart(this.progressChartTarget, doughnutChartOptions);
  }

  renderTasksStatusChart() {
    if (!this.hasTasksChartTarget) return;

    const emptyData = JSON.parse(this.tasksChartTarget.dataset.empty);
    const chartData = JSON.parse(this.tasksChartTarget.dataset.chart);

    const {
      unstarted: { label: unstartedLabel, value: unstartedValue, color: unstartedColor } = {},
      in_progress: { label: inProgressLabel, value: inProgressValue, color: inProgressColor } = {},
      completed: { label: completedLabel, value: completedValue, color: completedColor } = {},
      center_labels: {
        primary: { value: centerLabel } = {},
        secondary: { value: centerSubLabel, color: centerSubLabelColor } = {},
      } = {}
    } = chartData;

    let dataValues = [completedValue, inProgressValue, unstartedValue];
    let labels = [completedLabel, inProgressLabel, unstartedLabel];
    let labelColors = [completedColor, inProgressColor, unstartedColor];

    const total = dataValues.reduce((sum, val) => sum + val, 0);
    if (total === 0) {
      dataValues = emptyData.values;
      labelColors = emptyData.colors;
      labels = [];
    }

    const doughnutChartOptions = {
      dataValues,
      labels,
      labelColors,
      centerLabel,
      centerSubLabel,
      centerSubLabelColor,
    };

    this.renderDoughnutChart(this.tasksChartTarget, doughnutChartOptions);
  }

  renderProgramStatusChart() {
    if (!this.hasProgramStatusesChartTarget) return;

    const emptyData = JSON.parse(this.programStatusesChartTarget.dataset.empty);
    const chartData = JSON.parse(this.programStatusesChartTarget.dataset.chart);

    const {
      unstarted: { label: unstartedLabel, value: unstartedValue, color: unstartedColor } = {},
      in_progress: { label: inProgressLabel, value: inProgressValue, color: inProgressColor } = {},
      completed: { label: completedLabel, value: completedValue, color: completedColor } = {},
      center_labels: {
        primary: { value: centerLabel } = {},
        secondary: { value: centerSubLabel, color: centerSubLabelColor } = {},
      } = {}
    } = chartData;

    let dataValues = [completedValue, inProgressValue, unstartedValue];
    let labels = [completedLabel, inProgressLabel, unstartedLabel];
    let labelColors = [completedColor, inProgressColor, unstartedColor];

    const total = dataValues.reduce((sum, val) => sum + val, 0);
    if (total === 0) {
      dataValues = emptyData.values;
      labelColors = emptyData.colors;
      labels = [];
    }

    const doughnutChartOptions = {
      dataValues,
      labels,
      labelColors,
      centerLabel,
      centerSubLabel,
      centerSubLabelColor,
    };

    this.renderDoughnutChart(this.programStatusesChartTarget, doughnutChartOptions);
  }

  renderAccumulatedProgressionChart() {
    if (!this.hasAccumulatedProgressionTarget) return;

    const data = JSON.parse(this.accumulatedProgressionTarget.dataset.chart);

    const lineChartOptions = {
      labels: null, // if labels are not provided, Chart.js will use the index of the data points
      datasets: [
        {
          label: data.actual.legend,
          data: data.actual.data,
          borderColor: data.actual.color,
          borderWidth: 3,
          pointRadius: 0,  // No points on the line
          tension: 0.4, // Rounded lines during line curving
          borderJoinStyle: 'round',
          cubicInterpolationMode: 'default',
          spanGaps: true, // If true, lines will be drawn between points with no or null data
          backgroundColor: data.actual.background_color,
          fill: true
        },
        {
          label: data.expected.legend,
          data: data.expected.data,
          borderColor: data.expected.color,
          borderWidth: 1.5,
          pointRadius: 0,  // No points on the line
          tension: 0.125,
          borderDash: [3], // dotted line
          spanGaps: true, // If true, lines will be drawn between points with no or null data
          fill: false
        }
      ]
    };

    this.renderLinerChart(this.accumulatedProgressionTarget, lineChartOptions)
  }

  renderDoughnutChart(target, options = {}) {
    const canvas = target.getContext('2d');

    const data = {
      labels: options.labels,
      datasets: [{
        data: options.dataValues,
        backgroundColor: options.labelColors,
        borderWidth: 0
      }]
    };

    const config = {
      type: 'doughnut',
      data: data,
      options: {
        responsive: true, // adapt to container size
        maintainAspectRatio: false, // fills container even if aspect ratio changes
        cutout: '80%',
        rotation: 210,        // Start at 7 o'clock
        circumference: 300,   // 7 to 5 o'clock
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const value = context.raw;

                return options.displayPercentage ? `${value}%` : value;
              }
            },
            filter: function(context) {
              // Prevents tooltip from showing at all if label is empty
              return !!context.label && context.label.trim() !== '';
            }
          }
        }
      },
      plugins: [{ // Custom plugin to draw text in the center
        id: 'centerText',
        beforeDraw(chart) {
          const { width, height } = chart;
          const ctx = chart.ctx; // canvas context
          ctx.save();

          const lines = [options.centerLabel, options.centerSubLabel];
          const fontSizes = [36, 16]; // top line larger
          const fontWeights = ['bold', 'normal']; // top line bold
          const colors = ['#595959', options.centerSubLabelColor];
          const lineSpacing = 6;

          // Calculate total text block height
          const totalHeight = fontSizes[0] + fontSizes[1] + lineSpacing;

          let offsetY = 8; // Adjust this value to move it further down
          let y = (height - totalHeight) / 2 + offsetY;

          lines.forEach((text, i) => {
            ctx.font = `${fontWeights[i]} ${fontSizes[i]}px sans-serif`;
            ctx.fillStyle = colors[i];
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            ctx.fillText(text, width / 2, y);

            y += fontSizes[i] + lineSpacing;
          });

          ctx.restore();
        }
      }]
    };

    new Chart(canvas, config);
  }

  renderLinerChart(target, data = {}) {
    const canvas = target.getContext('2d');

    const config = {
      type: 'line',
      data: data,
      options: {
        responsive: true, // adapt to container size
        maintainAspectRatio: false, // fills container even if aspect ratio changes
        scales: {
          y: {
            min: 0,
            max: 100,
            ticks: {
              stepSize: 20,
              callback: value => value + '%'
            },
          },
          x: {
            type: 'time', // requires chartjs-adapter-luxon, use to format the date as May 21 instead of '2025-05-21'
            time: {
              unit: 'day',
              stepSize: 5,
              tooltipFormat: 'DDD', // Tooltip format
              displayFormats: {
                day: 'MMM d', // Display format for x-axis labels
              },
            },
            ticks: {
              autoSkip: true, // Automatically skips x-axis labels to avoid overlap
              maxTicksLimit: 20
            },
            grid: {
              display: false, // removes the grid lines from x axis
            },
          }
        },
        plugins: {
          tooltip: {
            mode: 'nearest',
            intersect: false,
            callbacks: {
              title: function(context) {
                return context[0].label;
              },
              label: function(context) {
                const label = context.dataset.label || '';
                const value = Math.round(context.parsed.y * 100) / 100;
                return `${label}: ${value}%`;
              }
            }
          },
          legend: {
            labels: {
              usePointStyle: true
            }
          }
        }
      }
    };

    new Chart(canvas, config);
  }
}
